import { viewerStrings } from '../copy/strings.js';
import { message, hasText, optionalText, CopyError } from '../copy/text.js';
import {
  lazy,
  Suspense,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  AllCommunityModule,
  ModuleRegistry,
  themeQuartz,
  colorSchemeDark,
  colorSchemeLight,
  type ColDef,
  type IDatasource,
  type GridApi,
  type IRowNode,
} from 'ag-grid-community';
import Alert from '@cloudscape-design/components/alert';
import Modal from '@cloudscape-design/components/modal';
import KeyValuePairs from '@cloudscape-design/components/key-value-pairs';
import type { QueryRecord, RelationshipQueryRow, RelationshipCounts } from '@cdr-clarity/contracts';
import type { Theme } from '../themes/catalog.js';
import type { UnifiedRecordsState } from '../state/unified-records-state.js';
import { pageSizes } from '../state/view-preferences.js';
import { cellText, cellHelp, fieldTitle } from '../presentation/cells.js';
import { ColumnLabel } from './ColumnLabel.js';
import { SelectionHeader, type SelectionHeaderParams } from './SelectionHeader.js';
import {
  changeTableSelection,
  tableColumnState,
  tableSourceId,
  togglePinnedAssociation,
  refreshPinnedAssociations,
  type PinnedAssociation,
} from './record-table-state.js';
import type { ExecuteRelationshipQuery } from '../state/unified-record-page.js';
import { FreezeIcon } from './FreezeIcon.js';
import { attachPageSticky } from './ag-page-sticky.js';
import { useCorrelatedRecords } from './useCorrelatedRecords.js';
import type { FindRelated } from '../state/related-records.js';
import type { ExecuteQuery } from '../state/record-page.js';
import { recordPage } from '../state/record-page.js';
import { baseQuery } from '@cdr-clarity/query-service';
import './table.css';

const RelationshipFlow = lazy(() =>
  import('./RelationshipFlow.js').then((module) => ({ default: module.RelationshipFlow })),
);

ModuleRegistry.registerModules([AllCommunityModule]);

/** Selected AG Grid renderer, now supplied exclusively by the production record state. */
export function RecordTable({
  store,
  design,
  findRelated,
  execute,
  executeRelationships,
  active = true,
  onOpenFields,
}: {
  store: UnifiedRecordsState;
  design: Theme;
  findRelated: FindRelated;
  execute: ExecuteQuery;
  executeRelationships: ExecuteRelationshipQuery;
  active?: boolean;
  onOpenFields?: () => void;
}) {
  const state = useSyncExternalStore(store.subscribe, store.getSnapshot);
  const fieldState = useSyncExternalStore(store.fields.subscribe, store.fields.getSnapshot);
  const [error, setError] = useState('');
  const [counts, setCounts] = useState<RelationshipCounts | null>(null);
  const [detail, setDetail] = useState<{ record: QueryRecord; collection: 'cdr' | 'cmr' } | null>(
    null,
  );
  const [detailView, setDetailView] = useState<'fields' | 'relationships'>('fields');
  const detailRequest = useRef(0);
  const grid = useRef<GridApi<RelationshipQueryRow> | null>(null);
  const host = useRef<HTMLElement>(null);
  const disposeSticky = useRef<(() => void) | undefined>(undefined);
  const synchronizing = useRef(false);
  const savedReady = useRef(false);
  const { preference } = fieldState;
  const [pins, setPins] = useState<readonly PinnedAssociation[]>([]);
  const [pinView, setPinView] = useState<{ key: string; rows: RelationshipQueryRow[] }>({
    key: '',
    rows: [],
  });
  const pinKey = JSON.stringify([
    state.scope,
    state.generation,
    pins,
    state.visibleFields.map((field) => field.id),
  ]);
  useEffect(() => {
    setPins([]);
  }, [store]);
  useEffect(() => {
    let active = true;
    if (pins.length) {
      void Promise.resolve()
        .then(() =>
          refreshPinnedAssociations(
            (query) =>
              active
                ? executeRelationships(query)
                : Promise.reject(
                    new CopyError(viewerStrings.webComponentsRecordTable.frozenRowQueryChanged),
                  ),
            store.definition(),
            state.visibleFields.map((field) => field.id),
            pins,
          ),
        )
        .then((rows) => {
          if (active) {
            setError('');
            setPinView({ key: pinKey, rows });
          }
        })
        .catch((reason: unknown) => {
          if (active)
            setError(
              message(viewerStrings.webComponentsRecordTable.couldNotRefreshFrozenRows, {
                value1: String(reason),
              }),
            );
        });
    }
    return () => {
      active = false;
    };
  }, [store, executeRelationships, pinKey]);
  const togglePin = useCallback(
    (row: RelationshipQueryRow) => {
      try {
        setPins(togglePinnedAssociation(pins, row));
      } catch (reason) {
        setError(String(reason));
      }
    },
    [pins],
  );
  const syncSelection = useCallback(
    (api: GridApi<RelationshipQueryRow>) => {
      const selected = store.getSnapshot().selected;
      const select: IRowNode<RelationshipQueryRow>[] = [];
      const deselect: IRowNode<RelationshipQueryRow>[] = [];
      api.forEachNode((node) => {
        if (!node.data) return;
        const value = selected.has(tableSourceId(node.data));
        if (node.isSelected() !== value) (value ? select : deselect).push(node);
      });
      if (select.length) api.setNodesSelected({ nodes: select, newValue: true, source: 'api' });
      if (deselect.length)
        api.setNodesSelected({ nodes: deselect, newValue: false, source: 'api' });
    },
    [store],
  );
  useEffect(() => {
    if (grid.current) syncSelection(grid.current);
  }, [state.selected, syncSelection]);
  const related = useCorrelatedRecords({
    find: findRelated,
    execute,
    scope: state.scope,
    fields: fieldState.visible,
    timezone: preference.timezone,
    contextKey: `${state.generation}:${active}`,
    focus: (ids) => store.focus(ids),
    inspect: (record, collection) => {
      setDetail({ record, collection });
      setDetailView('fields');
    },
    onError: setError,
  });
  useEffect(() => {
    if (!active) {
      ++detailRequest.current;
      setDetail(null);
    }
  }, [active]);
  const detailScope = useMemo(
    () => ({ ...state.scope, collection: detail?.collection ?? state.scope.collection }),
    [state.scope, detail?.collection],
  );
  useEffect(() => {
    ++detailRequest.current;
    setDetail(null);
  }, [state.generation]);
  async function navigateDetail(id: string, collection: 'cdr' | 'cmr'): Promise<void> {
    const request = ++detailRequest.current;
    try {
      const result = await recordPage(
        execute,
        baseQuery({ ...state.scope, collection, recordIds: [id], excludedRecordIds: [] }),
        fieldState.visible
          .filter((field) => field.collections.includes(collection))
          .map((field) => field.id),
        null,
      );
      const record = result.result.records.find((item) => item.id === id);
      if (!record)
        throw new CopyError(
          viewerStrings.webComponentsRecordTable.theSelectedRelationshipTargetIsUnavailableIn,
        );
      if (request === detailRequest.current) {
        setError('');
        setDetail({ record, collection });
      }
    } catch (reason) {
      if (request === detailRequest.current) setError(String(reason));
    }
  }
  const columns = useMemo<ColDef<RelationshipQueryRow>[]>(() => {
    const layout = tableColumnState(state.visibleFields, preference);
    const first = layout[0]?.colId;
    return layout.map((column) => {
      const id = column.colId;
      // tableColumnState emits only IDs from these same visible fields.
      const field = state.visibleFields.find((candidate) => candidate.id === id)!;
      const width = column.width;
      return {
        colId: id,
        headerName: fieldTitle(field, preference.timezone),
        headerTooltip: field.description,
        cellDataType: false,
        initialPinned: column.pinned ?? false,
        filter: false,
        ...(typeof width === 'number' ? { initialWidth: width } : {}),
        valueGetter: ({ data }) => data?.cells[id]?.value ?? null,
        valueFormatter: ({ data }) => cellText(data?.cells[id], field, preference.timezone),
        tooltipValueGetter: ({ data }) => cellHelp(data?.cells[id], field, preference.timezone),
        ...(id === first
          ? {
              cellRenderer: ({ data }: { data?: RelationshipQueryRow }) => (
                <span className="record-cell-actions">
                  <span>{cellText(data?.cells[id], field, preference.timezone)}</span>
                  {data &&
                    (hasText(
                      data.primary
                        ? data.related
                          ? viewerStrings.webComponentsRecordTable.callMedia
                          : viewerStrings.webComponentsRecordTable.call
                        : viewerStrings.webComponentsRecordTable.unmatchedMedia,
                    ) ? (
                      <small>
                        {data.primary
                          ? data.related
                            ? viewerStrings.webComponentsRecordTable.callMedia
                            : viewerStrings.webComponentsRecordTable.call
                          : viewerStrings.webComponentsRecordTable.unmatchedMedia}
                      </small>
                    ) : null)}
                  {data &&
                    data.relatedCalls.count > 0 &&
                    (() => {
                      const source = data.primary ?? data.related!;
                      return related.renderIcon({
                        data: { id: source.id, sources: source.sources, role: null, cells: {} },
                        collection: source.collection,
                      });
                    })()}
                  {data && (
                    <FreezeIcon
                      active={pins.some((pin) => pin.membershipId === data.membershipId)}
                      label={optionalText(
                        pins.some((pin) => pin.membershipId === data.membershipId)
                          ? viewerStrings.webComponentsRecordTable.unfreezeRow
                          : viewerStrings.webComponentsRecordTable.freezeRow,
                      )}
                      onClick={() => togglePin(data)}
                      accessibleLabel={optionalText(
                        pins.some((pin) => pin.membershipId === data.membershipId)
                          ? viewerStrings.webIconAccessibility.webComponentsRecordTableunfreezeRow
                          : viewerStrings.webIconAccessibility.webComponentsRecordTablefreezeRow,
                      )}
                    />
                  )}
                </span>
              ),
            }
          : {}),
      } satisfies ColDef<RelationshipQueryRow>;
    });
  }, [state.visibleFields, preference, related.renderIcon, pins, togglePin]);
  const theme = useMemo(
    () =>
      themeQuartz.withPart(design.dark ? colorSchemeDark : colorSchemeLight).withParams({
        fontFamily: design.tableFont,
        fontSize: design.tableSize,
        spacing: 4,
        cellHorizontalPadding: 10,
        rowHeight: 34,
        headerHeight: 38,
        backgroundColor: design.panel,
        foregroundColor: design.ink,
        accentColor: design.color,
        borderColor: design.line,
        headerBackgroundColor: design.tint,
        headerTextColor: design.ink,
        oddRowBackgroundColor: design.dark ? design.page : design.panel,
        browserColorScheme: design.dark ? 'dark' : 'light',
        wrapperBorderRadius: design.layout === 'console' || design.layout === 'focus' ? 0 : 6,
      }),
    [design],
  );
  const datasource = useMemo<IDatasource>(() => {
    let disposed = false;
    return {
      getRows(params) {
        void store
          .page(
            params.startRow,
            params.endRow - params.startRow,
            params.sortModel.map((item) => ({ key: item.colId, direction: item.sort })),
          )
          .then((page) => {
            if (!disposed) {
              setError('');
              setCounts(page.result.counts);
              params.successCallback([...page.result.rows], page.result.counts.memberships);
            }
          })
          .catch((reason: unknown) => {
            if (!disposed) {
              setError(reason instanceof Error ? reason.message : String(reason));
              params.failCallback();
            }
          });
      },
      destroy() {
        disposed = true;
      },
    };
  }, [store, state.generation]);
  useEffect(() => () => disposeSticky.current?.(), []);
  function saveLayout(): void {
    if (!grid.current || synchronizing.current || !savedReady.current) return;
    const saved = grid.current
      .getColumnState()
      .filter((column) => state.availableFields.some((field) => field.id === column.colId));
    const order = saved.map((column) => column.colId);
    const widths = Object.fromEntries(
      saved.flatMap((column) =>
        typeof column.width === 'number' ? [[column.colId, column.width]] : [],
      ),
    );
    const pinned = {
      ...preference.pinned,
      ...Object.fromEntries(
        saved.map((column) => [
          column.colId,
          column.pinned === 'right' ? ('right' as const) : column.pinned ? ('left' as const) : null,
        ]),
      ),
    };
    if (
      JSON.stringify(order) === JSON.stringify(preference.order) &&
      JSON.stringify(widths) === JSON.stringify(preference.widths) &&
      JSON.stringify(pinned) === JSON.stringify(preference.pinned)
    )
      return;
    try {
      store.fields.update({ order, widths, pinned });
    } catch (reason) {
      setError(String(reason));
    }
  }
  return (
    <section ref={host} className="cloudscape-lab" data-testid="record-table">
      {state.focus !== null && (
        <div className="correlation-scope" data-copy-group>
          {hasText(
            viewerStrings.webComponentsRecordTable.showingTheSelectedRecordAndItsEstablished,
          ) ? (
            <span>
              {viewerStrings.webComponentsRecordTable.showingTheSelectedRecordAndItsEstablished}
            </span>
          ) : null}
          {hasText(viewerStrings.webComponentsRecordTable.returnToResults) ? (
            <button type="button" onClick={() => store.returnToResults()}>
              {viewerStrings.webComponentsRecordTable.returnToResults}
            </button>
          ) : null}
        </div>
      )}
      {hasText(error) && <Alert type="error">{error}</Alert>}
      {counts &&
        (hasText(
          viewerStrings.webComponentsRecordTable.callsMediaObservationsRowsUnmatchedMedia,
        ) ? (
          <p className="record-count">
            {message(
              viewerStrings.webComponentsRecordTable.callsMediaObservationsRowsUnmatchedMedia,
              {
                value1: counts.primaryRecords.toLocaleString(),
                value2: counts.relatedRecords.toLocaleString(),
                value3: ' ',
                value4: counts.memberships.toLocaleString(),
                value5: ' ',
                value6: counts.unmatchedRelated.toLocaleString(),
              },
            )}
          </p>
        ) : null)}
      <AgGridReact<RelationshipQueryRow>
        theme={theme}
        columnDefs={columns}
        rowModelType="infinite"
        datasource={datasource}
        defaultColDef={{
          sortable: true,
          resizable: true,
          minWidth: 100,
          maxWidth: 460,
          filter: false,
          suppressHeaderMenuButton: true,
          headerComponentParams: { innerHeaderComponent: ColumnLabel },
        }}
        domLayout="autoHeight"
        pagination
        paginationPageSize={preference.pageSize}
        paginationPageSizeSelector={[...pageSizes]}
        cacheBlockSize={250}
        maxBlocksInCache={2}
        maxConcurrentDatasourceRequests={1}
        suppressDragLeaveHidesColumns
        getRowId={({ data, rowPinned }) => JSON.stringify([rowPinned ?? 'body', data.membershipId])}
        pinnedTopRowData={pins.length && pinView.key === pinKey ? pinView.rows : []}
        rowSelection={{ mode: 'multiRow', headerCheckbox: false }}
        selectionColumnDef={{
          pinned: 'left',
          width: 48,
          minWidth: 48,
          maxWidth: 48,
          ...(onOpenFields
            ? {
                headerComponent: SelectionHeader,
                headerComponentParams: {
                  onOpenFields,
                  accessibleLabel:
                    viewerStrings.webIconAccessibility.webComponentsRecordTableShowHideColumns,
                  title: viewerStrings.webComponentsFieldsDialog.showHideColumns,
                } satisfies Partial<SelectionHeaderParams>,
              }
            : {}),
        }}
        onGridReady={({ api }) => {
          grid.current = api;
          try {
            disposeSticky.current = attachPageSticky(host.current!);
          } catch (reason) {
            setError(String(reason));
          }
        }}
        onFirstDataRendered={({ api }) => {
          synchronizing.current = true;
          api.autoSizeAllColumns();
          api.applyColumnState({
            state: tableColumnState(state.visibleFields, preference),
            applyOrder: true,
          });
          synchronizing.current = false;
          savedReady.current = true;
        }}
        onColumnResized={(event) => {
          if (event.finished && ['uiColumnResized', 'autosizeColumns'].includes(event.source))
            saveLayout();
        }}
        onColumnMoved={(event) => {
          if (event.finished) saveLayout();
        }}
        onColumnPinned={saveLayout}
        onSortChanged={({ api }) => {
          if (synchronizing.current) return;
          const sort = api
            .getColumnState()
            .filter((column) => column.sort)
            .sort((a, b) => (a.sortIndex ?? 0) - (b.sortIndex ?? 0))
            .map((column) => ({ key: column.colId, direction: column.sort! }));
          if (JSON.stringify(sort) !== JSON.stringify(preference.sort))
            store.fields.update({ sort });
        }}
        onPaginationChanged={(event) => {
          if (event.newPageSize)
            store.fields.update({ pageSize: event.api.paginationGetPageSize() });
        }}
        onRowSelected={({ data, node, source }) => {
          const current = store.getSnapshot().selected;
          const selected = changeTableSelection(current, data, node.isSelected(), source);
          if (selected !== current) store.select(selected);
        }}
        onSelectionChanged={({ api }) => syncSelection(api)}
        onModelUpdated={({ api }) => syncSelection(api)}
        onRowDoubleClicked={({ data }) => {
          if (data) {
            const source = data.primary ?? data.related!;
            void navigateDetail(source.id, source.collection);
            setDetailView('fields');
          }
        }}
      />
      <Modal
        visible={detail !== null}
        onDismiss={() => {
          ++detailRequest.current;
          setDetail(null);
        }}
        header={optionalText(viewerStrings.webComponentsRecordTable.recordDetails)}
        size="max"
      >
        {detail && (
          <>
            <div
              className="field-tools"
              data-copy-group
              aria-label={viewerStrings.webComponentsRecordTable.recordDetailView}
            >
              {hasText(viewerStrings.webComponentsRecordTable.fields) ? (
                <button
                  aria-pressed={detailView === 'fields'}
                  onClick={() => setDetailView('fields')}
                >
                  {viewerStrings.webComponentsRecordTable.fields}
                </button>
              ) : null}
              {hasText(viewerStrings.webComponentsRecordTable.relationships) ? (
                <button
                  aria-pressed={detailView === 'relationships'}
                  onClick={() => setDetailView('relationships')}
                >
                  {viewerStrings.webComponentsRecordTable.relationships}
                </button>
              ) : null}
            </div>
            {detailView === 'relationships' ? (
              <Suspense
                fallback={
                  hasText(viewerStrings.webComponentsRecordTable.loadingRelationshipView) ? (
                    <p role="status">
                      {viewerStrings.webComponentsRecordTable.loadingRelationshipView}
                    </p>
                  ) : null
                }
              >
                <RelationshipFlow
                  find={findRelated}
                  execute={execute}
                  scope={detailScope}
                  recordId={detail.record.id}
                  fields={fieldState.visible}
                  timezone={preference.timezone}
                  onNavigate={(id, collection) => void navigateDetail(id, collection)}
                />
              </Suspense>
            ) : (
              <KeyValuePairs
                columns={3}
                items={fieldState.visible
                  .filter((field) => field.collections.includes(detail.collection))
                  .map((field) => ({
                    label: fieldTitle(field, preference.timezone),
                    value: (
                      <span
                        title={optionalText(
                          cellHelp(detail.record.cells[field.id], field, preference.timezone),
                        )}
                      >
                        {cellText(detail.record.cells[field.id], field, preference.timezone)}
                        {hasText(viewerStrings.webComponentsRecordTable.original) ? (
                          <small>
                            {message(viewerStrings.webComponentsRecordTable.original, {
                              value1: String(
                                detail.record.cells[field.id]?.raw ??
                                  viewerStrings.webPresentationCells.missing,
                              ),
                            })}
                          </small>
                        ) : null}
                      </span>
                    ),
                  }))}
              />
            )}
          </>
        )}
      </Modal>
      {related.popup}
    </section>
  );
}
